<template>
  <onboarding-layout>
    <div class="step-top">
      <div class="step-indicator">
        <span>Etapa <strong>{{ currentStep }}</strong> de {{ totalSteps }}</span>
        <span class="step-indicator__sep">·</span>
        <span class="text-muted">Conhecendo seu cenário</span>
      </div>
      <div
        class="step-progress"
        role="progressbar"
        :aria-valuenow="currentStep"
        aria-valuemin="1"
        :aria-valuemax="totalSteps">
        <div
          class="step-progress__fill"
          :style="{ width: progressPercent + '%' }" />
      </div>
    </div>

    <div class="step-content">
      <div class="greeting-row">
        <h1 class="greeting">
          Bem-vindo(a)<template v-if="firstName">, {{ firstName }}</template>.
        </h1>
      </div>

      <transition name="step-fade" mode="out-in">
      <!-- Step 1 — CRM -->
      <div v-if="currentStep === 1" key="step-1" class="card-onboarding">
        <div class="card-onboarding__header">
          <div class="card-onboarding__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M6 22V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v18Z" />
              <path d="M6 12H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h2" />
              <path d="M18 9h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2h-2" />
              <path d="M10 6h4" />
              <path d="M10 10h4" />
              <path d="M10 14h4" />
              <path d="M10 18h4" />
            </svg>
          </div>
          <div>
            <h2 class="card-onboarding__title">Qual CRM sua equipe usa hoje?</h2>
            <p class="card-onboarding__subtitle">Vamos preparar a integração certa para chegar à sua primeira ligação.</p>
          </div>
        </div>

        <div class="form-group">
          <label class="card-onboarding__label">Selecione seu CRM</label>
          <onboarding-select
            v-model="selectedOption"
            :options="crmGroups"
            grouped
            placeholder="Escolha uma opção" />
        </div>

        <div v-if="isOtherSelected" class="form-group">
          <label for="crm-custom" class="card-onboarding__label">Nome do CRM</label>
          <input
            id="crm-custom"
            v-model="customCrmName"
            type="text"
            class="onboarding-input"
            placeholder="Digite o nome do seu CRM"
            maxlength="60">
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
          {{ errorMessage }}
        </div>

        <button
          type="button"
          class="onboarding-button"
          :disabled="!canContinueCrmStep || submitting"
          @click="submitCrmStep">
          <span v-if="!submitting">Continuar</span>
          <span v-else>Salvando…</span>
        </button>
      </div>

      <!-- Step 2 — instalação do Webphone -->
      <div v-else-if="currentStep === 2" key="step-2" class="card-onboarding">
        <div class="card-onboarding__header">
          <div class="card-onboarding__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>
          <div>
            <h2 class="card-onboarding__title">Instale e autorize o Webphone</h2>
          </div>
        </div>

        <div
          class="webphone-extension-card"
          :class="`webphone-extension-card--${installStepState}`">
          <span class="webphone-extension-card__status" aria-hidden="true">
            <i v-if="installStepState === 'done'" class="ik ik-check-circle" />
            <i v-else-if="installStepState === 'waiting'" class="ik ik-loader" />
            <i v-else class="ik ik-download" />
          </span>
          <div class="webphone-extension-card__icon" aria-hidden="true">
            <img
              src="/static/images/logo_small.png"
              alt="Webphone API4COM">
          </div>
          <div class="webphone-extension-card__body">
            <strong class="webphone-extension-card__name">Webphone API4COM</strong>
            <span class="webphone-extension-card__author">
              <i class="ik ik-check-circle" aria-hidden="true" />
              API4COM TECNOLOGIA E SERVICOS LTDA
            </span>
          </div>
          <a
            v-if="installStepState !== 'done'"
            class="webphone-extension-card__cta"
            :href="WEBPHONE_STORE_URL"
            target="_blank"
            rel="noopener noreferrer"
            @click="onOpenStoreClick">
            Instalar
          </a>
          <span
            v-else
            class="webphone-extension-card__cta webphone-extension-card__cta--done">
            Instalado
          </span>
        </div>

        <p class="webphone-intro">
          Falta pouco para sua primeira ligação.<br>
          Conclua os passos abaixo no Webphone API4COM instalado:
        </p>

        <ul class="webphone-checklist">
          <li
            class="webphone-checklist__item"
            :class="`webphone-checklist__item--${authStepState}`">
            <span class="webphone-checklist__indicator" aria-hidden="true">
              <i v-if="authStepState === 'done'" class="ik ik-check" />
              <i v-else-if="authStepState === 'waiting'" class="ik ik-loader" />
              <span v-else>1</span>
            </span>
            <div class="webphone-checklist__body">
              <strong class="webphone-checklist__title">Autenticar com sua conta API4COM</strong>
              <span class="webphone-checklist__helper">Faça login na extensão usando o mesmo e-mail e senha aqui do Portal.</span>
            </div>
          </li>
          <li
            class="webphone-checklist__item"
            :class="`webphone-checklist__item--${micStepState}`">
            <span class="webphone-checklist__indicator" aria-hidden="true">
              <i v-if="micStepState === 'done'" class="ik ik-check" />
              <i v-else-if="micStepState === 'waiting'" class="ik ik-loader" />
              <span v-else>2</span>
            </span>
            <div class="webphone-checklist__body">
              <strong class="webphone-checklist__title">Autorizar microfone</strong>
              <span class="webphone-checklist__helper">Necessário para fazer e receber ligações. A extensão pede a permissão ao logar.</span>
            </div>
          </li>
        </ul>

        <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
          {{ errorMessage }}
        </div>

        <div v-if="installAttempted && !autoDetectFailed && !isWebphoneStepReady" class="webphone-actions">
          <p class="webphone-actions__hint" role="status">
            <i class="ik ik-loader" aria-hidden="true" />
            Aguardando…
          </p>
        </div>

        <div v-if="!isWebphoneStepReady && autoDetectFailed" class="webphone-fallback">
          <p class="webphone-fallback__text">{{ fallbackMessage }}</p>
          <button
            type="button"
            class="onboarding-button onboarding-button--ghost"
            :disabled="submitting"
            @click="submitWebphoneManualFallback">
            <span v-if="!submitting">Já instalei, continuar</span>
            <span v-else>Salvando…</span>
          </button>
        </div>

        <button
          v-if="isWebphoneStepReady"
          type="button"
          class="onboarding-button"
          :disabled="submitting"
          @click="submitWebphoneAuto">
          <span v-if="!submitting">Continuar</span>
          <span v-else>Salvando…</span>
        </button>
      </div>

      <!-- Step 3 — teste de rede -->
      <div v-else-if="currentStep === 3" key="step-3" class="card-onboarding">
        <div class="card-onboarding__header">
          <div class="card-onboarding__icon">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true">
              <path d="M5 12.55a11 11 0 0 1 14.08 0" />
              <path d="M1.42 9a16 16 0 0 1 21.16 0" />
              <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
              <line x1="12" y1="20" x2="12.01" y2="20" />
            </svg>
          </div>
          <div>
            <h2 class="card-onboarding__title">Vamos testar sua rede</h2>
            <p class="card-onboarding__subtitle">Medimos ping, jitter e estabilidade da sua conexão.</p>
          </div>
        </div>

        <p class="network-test-intro">
          Vamos medir três indicadores essenciais para uma ligação de voz com qualidade.
        </p>

        <ul class="network-test-metrics-grid" :class="networkTestResult && `network-test-metrics-grid--${networkTestResult.color}`">
          <li class="network-test-metric">
            <div class="network-test-metric__label">
              <i class="ik ik-activity" aria-hidden="true" />
              <span>Latência</span>
              <span class="network-test-metric__info" tabindex="0" aria-label="Sobre latência">
                <i class="ik ik-info" aria-hidden="true" />
                <span class="network-test-metric__tooltip" role="tooltip">{{ NETWORK_METRIC_HELP.latency }}</span>
              </span>
            </div>
            <div class="network-test-metric__value">
              <span v-if="networkTestRunning" class="network-test-metric__placeholder">—</span>
              <span v-else-if="networkTestResult && networkTestResult.latencyMs !== null">{{ networkTestResult.latencyMs }}</span>
              <span v-else class="network-test-metric__placeholder">—</span>
              <span class="network-test-metric__unit">ms</span>
            </div>
          </li>
          <li class="network-test-metric">
            <div class="network-test-metric__label">
              <i class="ik ik-bar-chart-2" aria-hidden="true" />
              <span>Jitter</span>
              <span class="network-test-metric__info" tabindex="0" aria-label="Sobre jitter">
                <i class="ik ik-info" aria-hidden="true" />
                <span class="network-test-metric__tooltip" role="tooltip">{{ NETWORK_METRIC_HELP.jitter }}</span>
              </span>
            </div>
            <div class="network-test-metric__value">
              <span v-if="networkTestRunning" class="network-test-metric__placeholder">—</span>
              <span v-else-if="networkTestResult && networkTestResult.jitterMs !== null">{{ networkTestResult.jitterMs }}</span>
              <span v-else class="network-test-metric__placeholder">—</span>
              <span class="network-test-metric__unit">ms</span>
            </div>
          </li>
          <li class="network-test-metric">
            <div class="network-test-metric__label">
              <i class="ik ik-package" aria-hidden="true" />
              <span>Perda de pacotes</span>
              <span class="network-test-metric__info" tabindex="0" aria-label="Sobre perda de pacotes">
                <i class="ik ik-info" aria-hidden="true" />
                <span class="network-test-metric__tooltip" role="tooltip">{{ NETWORK_METRIC_HELP.loss }}</span>
              </span>
            </div>
            <div class="network-test-metric__value">
              <span v-if="networkTestRunning" class="network-test-metric__placeholder">—</span>
              <span v-else-if="networkTestResult && networkTestResult.packetLossPct !== null">{{ networkTestResult.packetLossPct }}</span>
              <span v-else class="network-test-metric__placeholder">—</span>
              <span class="network-test-metric__unit">%</span>
            </div>
          </li>
        </ul>

        <div v-if="networkTestRunning" class="network-test-progress" role="status">
          <div class="network-test-progress__bar">
            <div
              class="network-test-progress__fill"
              :style="{ width: networkTestProgressPct + '%' }" />
          </div>
          <span class="network-test-progress__text">
            <template v-if="!networkTestOverdue">
              Medindo sua rede… {{ networkTestElapsedSec }}s / {{ networkTestEstimatedTotalSec }}s
            </template>
            <template v-else>
              Medindo sua rede… {{ networkTestElapsedSec }}s · sua conexão está lenta, aguarde
            </template>
          </span>
        </div>

        <div
          v-if="networkTestResult"
          class="network-test-summary"
          :class="`network-test-summary--${networkTestResult.color}`">
          <div class="network-test-summary__header">
            <i :class="networkTestResultIcon" aria-hidden="true" />
            <strong>{{ networkTestResultHeadline }}</strong>
          </div>
          <p class="network-test-summary__body">{{ networkTestResultBody }}</p>
        </div>

        <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
          {{ errorMessage }}
        </div>

        <div class="network-test-actions">
          <button
            v-if="!networkTestResult"
            type="button"
            class="onboarding-button"
            :class="{ 'onboarding-button--measuring': networkTestRunning }"
            :disabled="networkTestRunning"
            @click="runNetworkTest">
            <template v-if="networkTestRunning">
              <i class="ik ik-loader" aria-hidden="true" />
              Medindo…
            </template>
            <span v-else>Iniciar teste</span>
          </button>
          <template v-else>
            <button
              type="button"
              class="onboarding-button"
              :disabled="submitting"
              @click="submitNetworkTestStep">
              <span v-if="!submitting">
                <i class="ik ik-check-circle" aria-hidden="true" />
                {{ networkTestAdvanceLabel }}
              </span>
              <span v-else>Salvando…</span>
            </button>
            <a
              v-if="showSupportLink"
              :href="SUPPORT_WHATSAPP_URL"
              target="_blank"
              rel="noopener noreferrer"
              class="network-test-actions__support">
              <i class="ik ik-message-circle" aria-hidden="true" />
              Falar com suporte
            </a>
            <button
              type="button"
              class="onboarding-button onboarding-button--ghost"
              :disabled="networkTestRunning || submitting"
              @click="runNetworkTest">
              Refazer teste
            </button>
          </template>
        </div>
      </div>
      </transition>

      <div class="step-dots">
        <span
          v-for="n in totalSteps"
          :key="n"
          class="step-dots__dot"
          :class="{ 'step-dots__dot--active': n === currentStep }" />
      </div>

      <div class="step-footer">
        <p class="step-footer__objective">
          <span class="text-muted">Objetivo:</span>
          <strong>fazer sua primeira ligação hoje</strong>
        </p>
        <a
          href="https://wa.me/+554833328503"
          target="_blank"
          rel="noopener"
          class="step-footer__help">Precisa de ajuda?</a>
      </div>
    </div>
  </onboarding-layout>
</template>

<script>
import OnboardingLayout from '@/components/Onboarding/OnboardingLayout'
import { friendlyPlainText } from '@/utils/error-message'
import OnboardingSelect from '@/components/Onboarding/OnboardingSelect'
import { CRM_OPTION_OTHER, CRM_OPTION_NONE } from '@/services/crmList'
import onboardingService from '@/services/onboarding'
import { runNetworkTest, NETWORK_TEST_SAMPLE_COUNT, NETWORK_TEST_ESTIMATED_TOTAL_SEC } from '@/services/networkTest'
import { trackEvent, setTag } from '@/services/clarity'

const WEBPHONE_STORE_URL = 'https://chromewebstore.google.com/detail/webphone-api4com/ccahjcilejbllkkgngikflblpgojjdaa?authuser=0&hl=pt-BR'
const WEBPHONE_DETECT_POLL_MS = 8000
const WEBPHONE_DETECT_MAX_ATTEMPTS = 5

const NETWORK_METRIC_HELP = {
  latency: 'Latência: tempo que sua voz leva para chegar ao outro lado. Alta latência causa atrasos e conversas "atropeladas".',
  jitter: 'Jitter: variação na latência. Valores altos geram voz robotizada e cortes intermitentes.',
  loss: 'Perda de pacotes: pedaços de áudio que não chegam. Causa silêncios, eco e quedas de chamada.'
}

const NETWORK_TEST_TICK_MS = 200

const SUPPORT_WHATSAPP_URL = 'https://wa.me/+554833328503'

export default {
  name: 'OnboardingWizard',
  components: {
    OnboardingLayout,
    OnboardingSelect
  },
  data () {
    return {
      crms: [],
      selectedOption: null,
      customCrmName: '',
      submitting: false,
      errorMessage: null,
      totalSteps: 3,
      currentStep: 1,
      webphoneStepStarted: false,
      networkTestStepStarted: false,
      autoDetectFired: false,
      micDetectFired: false,
      installAttempted: false,
      autoDetectFailed: false,
      detectPollAttempts: 0,
      detectPollTimer: null,
      networkTestRunning: false,
      networkTestResult: null,
      networkTestError: null,
      networkTestStartedAt: 0,
      networkTestElapsedMs: 0,
      networkTestSamplesDone: 0,
      networkTestSamplesTotal: NETWORK_TEST_SAMPLE_COUNT,
      networkTestElapsedTimer: null,
      WEBPHONE_STORE_URL,
      NETWORK_METRIC_HELP,
      SUPPORT_WHATSAPP_URL
    }
  },
  async created () {
    await this.resumeStep()
    this.loadCrms()
    trackEvent('wizard_started')
    this.maybeFireWebphoneStepStarted()
  },
  mounted () {
    window.addEventListener('focus', this.refreshOnboardingOnFocus)
  },
  beforeDestroy () {
    window.removeEventListener('focus', this.refreshOnboardingOnFocus)
    this.stopWebphoneDetectPoll()
    if (this.networkTestElapsedTimer) {
      window.clearInterval(this.networkTestElapsedTimer)
      this.networkTestElapsedTimer = null
    }
  },
  watch: {
    currentStep () {
      this.maybeFireWebphoneStepStarted()
      this.maybeFireNetworkTestStepStarted()
    },
    isWebphoneInstalled (installed) {
      if (installed && this.currentStep === 2 && !this.autoDetectFired) {
        this.autoDetectFired = true
        trackEvent('webphone_install_detected')
      }
      if (this.currentStep === 2 && this.isWebphoneStepReady) {
        this.stopWebphoneDetectPoll()
      }
    },
    isMicrophoneAuthorized (authorized) {
      if (authorized && this.currentStep === 2 && !this.micDetectFired) {
        this.micDetectFired = true
        trackEvent('microphone_authorized_detected')
      }
      if (this.currentStep === 2 && this.isWebphoneStepReady) {
        this.stopWebphoneDetectPoll()
      }
    }
  },
  computed: {
    crmGroups () {
      return [
        { label: 'crms', items: this.crms },
        { label: 'pinned', items: [CRM_OPTION_OTHER, CRM_OPTION_NONE] }
      ]
    },
    firstName () {
      const fullName = this.$store.state.auth.user && this.$store.state.auth.user.name
      if (!fullName) return null
      return fullName.split(' ')[0]
    },
    isOtherSelected () {
      return this.selectedOption && this.selectedOption.value === 'Outros'
    },
    progressPercent () {
      if (this.totalSteps <= 1) return 0
      return ((this.currentStep - 1) / (this.totalSteps - 1)) * 100
    },
    canContinueCrmStep () {
      if (!this.selectedOption) return false
      if (this.isOtherSelected) return !!(this.customCrmName && this.customCrmName.trim())
      return true
    },
    onboarding () {
      return this.$store.state.auth.onboarding
    },
    isWebphoneInstalled () {
      return !!(this.onboarding && this.onboarding.webphoneInstalledAt)
    },
    isMicrophoneAuthorized () {
      return !!(this.onboarding && this.onboarding.microphoneAuthorizedAt)
    },
    isWebphoneStepReady () {
      return this.isWebphoneInstalled && this.isMicrophoneAuthorized
    },
    installStepState () {
      if (this.isWebphoneInstalled) return 'done'
      if (this.installAttempted) return 'done'
      return 'pending'
    },
    authStepState () {
      if (this.isWebphoneInstalled) return 'done'
      if (this.installAttempted) return 'waiting'
      return 'pending'
    },
    micStepState () {
      if (this.isMicrophoneAuthorized) return 'done'
      if (this.installAttempted) return 'waiting'
      return 'pending'
    },
    networkTestElapsedSec () {
      return Math.floor(this.networkTestElapsedMs / 1000)
    },
    networkTestEstimatedTotalSec () {
      return NETWORK_TEST_ESTIMATED_TOTAL_SEC
    },
    networkTestOverdue () {
      return this.networkTestElapsedSec > this.networkTestEstimatedTotalSec
    },
    networkTestProgressPct () {
      const byElapsed = (this.networkTestElapsedMs / (NETWORK_TEST_ESTIMATED_TOTAL_SEC * 1000)) * 100
      const bySamples = this.networkTestSamplesTotal
        ? (this.networkTestSamplesDone / this.networkTestSamplesTotal) * 100
        : 0
      const pct = Math.max(byElapsed, bySamples)
      return Math.min(Math.round(pct), 100)
    },
    fallbackMessage () {
      const webphoneMissing = !this.isWebphoneInstalled
      const micMissing = !this.isMicrophoneAuthorized
      if (webphoneMissing && micMissing) {
        return 'Não conseguimos confirmar a instalação do Webphone nem a autorização do microfone. Se você já instalou e autorizou, prossiga manualmente.'
      }
      if (webphoneMissing) {
        return 'Não conseguimos confirmar a instalação do Webphone. Se você já instalou, prossiga manualmente.'
      }
      return 'Não conseguimos confirmar a autorização do microfone. Se você já autorizou, prossiga manualmente.'
    },
    networkTestResultIcon () {
      if (!this.networkTestResult) return ''
      const map = {
        green: 'ik ik-check-circle',
        yellow: 'ik ik-alert-triangle',
        red: 'ik ik-alert-octagon',
        error: 'ik ik-x-circle'
      }
      return map[this.networkTestResult.color] || ''
    },
    networkTestResultHeadline () {
      if (!this.networkTestResult) return ''
      const map = {
        green: 'Conexão boa',
        yellow: 'Conexão instável',
        red: 'Conexão ruim',
        error: 'Não conseguimos completar o teste'
      }
      return map[this.networkTestResult.color] || ''
    },
    networkTestResultBody () {
      if (!this.networkTestResult) return ''
      const map = {
        green: 'Sua rede está pronta — esperamos ligações nítidas e estáveis.',
        yellow: 'Pode haver pequenas falhas durante a ligação. Você pode avançar, mas recomendamos falar com o suporte se quiser otimizar antes.',
        red: 'Sua experiência de ligação será prejudicada (cortes, eco e quedas). Você pode avançar por conta e risco — ou falar com o suporte para diagnosticar.',
        error: 'Houve uma falha técnica ao medir sua rede. Tente novamente ou fale com o suporte.'
      }
      return map[this.networkTestResult.color] || ''
    },
    networkTestAdvanceLabel () {
      if (!this.networkTestResult) return ''
      const map = {
        green: 'Continuar',
        yellow: 'Avançar mesmo assim',
        red: 'Avançar por minha conta e risco',
        error: 'Avançar mesmo assim'
      }
      return map[this.networkTestResult.color] || 'Continuar'
    },
    showSupportLink () {
      if (!this.networkTestResult) return false
      return ['yellow', 'red'].includes(this.networkTestResult.color)
    }
  },
  methods: {
    async resumeStep () {
      await this.$store.dispatch('auth/reloadOnboarding')
      const onboarding = this.$store.state.auth.onboarding
      const completed = (onboarding && onboarding.completedSteps) || []
      if (completed.includes('step-4')) {
        this.currentStep = 3
      } else if (completed.includes('step-1')) {
        this.currentStep = 2
      }
    },
    maybeFireWebphoneStepStarted () {
      if (this.currentStep === 2 && !this.webphoneStepStarted) {
        this.webphoneStepStarted = true
        trackEvent('step_4_started')
      }
    },
    maybeFireNetworkTestStepStarted () {
      if (this.currentStep === 3 && !this.networkTestStepStarted) {
        this.networkTestStepStarted = true
        trackEvent('step_5_started')
      }
    },
    refreshOnboardingOnFocus () {
      if (this.currentStep !== 2) return
      if (this.isWebphoneStepReady) return
      this.$store.dispatch('auth/reloadOnboarding')
    },
    onOpenStoreClick () {
      trackEvent('webphone_download_clicked')
      this.installAttempted = true
      this.startWebphoneDetectPoll()
    },
    startWebphoneDetectPoll () {
      if (this.detectPollTimer) return
      if (this.isWebphoneStepReady) return
      this.detectPollAttempts = 0
      this.autoDetectFailed = false
      this.detectPollTimer = window.setInterval(this.tickWebphoneDetectPoll, WEBPHONE_DETECT_POLL_MS)
    },
    async tickWebphoneDetectPoll () {
      if (this.isWebphoneStepReady) {
        this.stopWebphoneDetectPoll()
        return
      }
      this.detectPollAttempts++
      try {
        await this.$store.dispatch('auth/reloadOnboarding')
      } catch (_) { }
      if (this.isWebphoneStepReady) {
        this.stopWebphoneDetectPoll()
        return
      }
      if (this.detectPollAttempts >= WEBPHONE_DETECT_MAX_ATTEMPTS) {
        this.stopWebphoneDetectPoll()
        this.autoDetectFailed = true
      }
    },
    stopWebphoneDetectPoll () {
      if (this.detectPollTimer) {
        window.clearInterval(this.detectPollTimer)
        this.detectPollTimer = null
      }
    },
    async loadCrms () {
      try {
        const crms = await onboardingService.getCrms()
        this.crms = (crms || []).map(crm => ({ text: crm.name, value: crm.value }))
      } catch (err) {
        this.errorMessage = 'Não foi possível carregar a lista de CRMs. Tente novamente.'
      }
    },
    async submitCrmStep () {
      if (!this.canContinueCrmStep || this.submitting) return
      this.submitting = true
      this.errorMessage = null
      try {
        const crm = this.isOtherSelected
          ? this.customCrmName.trim()
          : this.selectedOption.value
        const state = await onboardingService.completeCrmStep({ crm })
        if (state && state.branch) setTag('branch', state.branch)
        trackEvent('step_1_completed')
        await this.$store.dispatch('auth/reloadOnboarding')
        this.currentStep = 2
      } catch (err) {
        this.errorMessage = friendlyPlainText(err, 'Não foi possível salvar agora. Tente novamente.')
      } finally {
        this.submitting = false
      }
    },
    async submitWebphoneAuto () {
      if (!this.isWebphoneStepReady || this.submitting) return
      await this.finalizeWebphoneStep({ manualConfirmation: false })
    },
    async submitWebphoneManualFallback () {
      if (this.submitting) return
      trackEvent('step_4_skipped_manual')
      await this.finalizeWebphoneStep({ manualConfirmation: true })
    },
    async finalizeWebphoneStep ({ manualConfirmation }) {
      this.submitting = true
      this.errorMessage = null
      try {
        await onboardingService.completeWebphoneStep({ manualConfirmation, finalizes: false })
        trackEvent('step_4_completed')
        await this.$store.dispatch('auth/reloadOnboarding')
        this.currentStep = 3
      } catch (err) {
        this.errorMessage = friendlyPlainText(err, 'Não foi possível salvar agora. Tente novamente.')
      } finally {
        this.submitting = false
      }
    },
    async runNetworkTest () {
      if (this.networkTestRunning) return
      this.networkTestRunning = true
      this.networkTestError = null
      this.networkTestResult = null
      this.networkTestSamplesDone = 0
      this.networkTestSamplesTotal = NETWORK_TEST_SAMPLE_COUNT
      this.networkTestElapsedMs = 0
      this.networkTestStartedAt = Date.now()
      this.networkTestElapsedTimer = window.setInterval(() => {
        this.networkTestElapsedMs = Date.now() - this.networkTestStartedAt
      }, NETWORK_TEST_TICK_MS)
      trackEvent('network_test_run')
      try {
        this.networkTestResult = await runNetworkTest({
          onProgress: ({ completed, total }) => {
            this.networkTestSamplesDone = completed
            this.networkTestSamplesTotal = total
          }
        })
      } catch (err) {
        this.networkTestError = friendlyPlainText(err, 'Não foi possível concluir o teste agora.')
        this.networkTestResult = {
          color: 'error',
          latencyMs: null,
          jitterMs: null,
          packetLossPct: null
        }
      } finally {
        this.networkTestRunning = false
        if (this.networkTestElapsedTimer) {
          window.clearInterval(this.networkTestElapsedTimer)
          this.networkTestElapsedTimer = null
        }
      }
    },
    async submitNetworkTestStep () {
      if (this.submitting) return
      if (!this.networkTestResult) return
      this.submitting = true
      this.errorMessage = null
      try {
        await onboardingService.completeNetworkTestStep({
          latencyMs: this.networkTestResult.latencyMs,
          jitterMs: this.networkTestResult.jitterMs,
          packetLossPct: this.networkTestResult.packetLossPct,
          color: this.networkTestResult.color
        })
        setTag('network_test_color', this.networkTestResult.color)
        trackEvent('step_5_completed')
        await this.$store.dispatch('auth/reloadOnboarding')
        this.celebrateAndGoHome()
      } catch (err) {
        this.errorMessage = friendlyPlainText(err, 'Não foi possível salvar agora. Tente novamente.')
      } finally {
        this.submitting = false
      }
    },
    celebrateAndGoHome () {
      try {
        const uuid = this.$store.state.auth.user && this.$store.state.auth.user.uuid
        if (uuid) window.localStorage.setItem(`celebration-pending:${uuid}`, '1')
      } catch (_) { }
      this.$router.push({ name: 'home' })
    }
  }
}
</script>

<style lang="scss" scoped>
.step-fade-enter-active,
.step-fade-leave-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}
.step-fade-enter {
  opacity: 0;
  transform: translateX(24px);
}
.step-fade-leave-to {
  opacity: 0;
  transform: translateX(-24px);
}

.step-content {
  width: 100%;
  max-width: 720px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-top: auto;
  margin-bottom: auto;
}

.step-top {
  width: 100%;
  max-width: 720px;
  margin-bottom: 24px;
}

.step-indicator {
  text-align: left;
  font-size: 0.9rem;
  color: #6b7280;
  margin-bottom: 8px;

  strong {
    color: #2563EB;
    font-weight: 600;
  }

  &__sep {
    margin: 0 8px;
    color: #d1d5db;
  }
}

.step-progress {
  width: 100%;
  height: 4px;
  background-color: #eef0f4;
  border-radius: 2px;
  overflow: hidden;

  &__fill {
    height: 100%;
    background-color: #2563EB;
    border-radius: 2px;
    transition: width 0.3s ease;
  }
}

.greeting-row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  margin: 8px 0 32px;
  flex-wrap: wrap;
}

.greeting {
  font-size: 2rem;
  font-weight: 700;
  color: #111827;
  text-align: left;
  margin: 0;
}

.card-onboarding {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  padding: 32px;

  &__header {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
  }

  &__icon {
    flex-shrink: 0;
    width: 48px;
    height: 48px;
    border-radius: 10px;
    background: #dbeafe;
    color: #2563EB;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.4rem;
  }

  &__title {
    font-size: 1.15rem;
    font-weight: 600;
    color: #111827;
    margin: 0 0 4px;
  }

  &__subtitle {
    font-size: 0.9rem;
    color: #6b7280;
    margin: 0;
  }

  &__label {
    display: block;
    font-size: 0.85rem;
    font-weight: 700;
    color: #111827;
    margin-bottom: 6px;
  }
}

.onboarding-input {
  display: block;
  width: 100%;
  padding: 7px 12px;
  font-size: 0.9rem;
  line-height: 1.4;
  color: #111827;
  background-color: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  transition: border-color 0.15s ease;

  &:focus {
    outline: none;
    border-color: #c7c9d9;
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.onboarding-button {
  display: inline-block;
  background-color: #2563EB;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  padding: 10px 20px;
  font-weight: 600;
  font-size: 0.95rem;
  line-height: 1.4;
  margin-top: 8px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  text-decoration: none;
  text-align: center;

  &:hover:not(:disabled) {
    background-color: #1D4ED8;
    text-decoration: none;
    color: #ffffff;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.25);
  }

  &:disabled {
    background-color: #c7c9d9;
    color: #ffffff;
    cursor: not-allowed;
  }

  &--ghost {
    background-color: transparent;
    color: #2563EB;
    border: 1px solid #c7c9d9;

    &:hover:not(:disabled) {
      background-color: #f3f4f6;
      color: #1D4ED8;
    }
  }

  &--measuring {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;

    &:disabled {
      background-color: rgba(99, 102, 241, 0.6);
      color: #ffffff;
      opacity: 1;
      cursor: progress;
    }

    i {
      animation: webphone-spin 1.2s linear infinite;
      font-size: 1rem;
    }
  }
}

.webphone-extension-card {
  position: relative;
  display: grid;
  grid-template-columns: auto 56px 1fr auto;
  align-items: center;
  gap: 14px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1px solid #eef0f4;
  background: #ffffff;
  margin-bottom: 16px;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &--done {
    border-color: #a7f3d0;
    background: #f0fdf4;
  }

  &--waiting {
    border-color: #bfdbfe;
    background: #eff6ff;
  }

  &__status {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #eef0f4;
    color: #6b7280;
    font-size: 1rem;

    .webphone-extension-card--waiting & {
      background: #dbeafe;
      color: #2563EB;

      i {
        animation: webphone-spin 1.2s linear infinite;
      }
    }

    .webphone-extension-card--done & {
      background: #16a34a;
      color: #ffffff;
    }
  }

  &__icon {
    width: 56px;
    height: 56px;
    border-radius: 10px;
    background: #ffffff;
    border: 1px solid #eef0f4;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
  }

  &__name {
    color: #111827;
    font-size: 0.95rem;
    font-weight: 700;
  }

  &__author {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    color: #2563EB;
    font-size: 0.8rem;
    font-weight: 600;

    i { font-size: 0.85rem; }
  }

  &__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    border-radius: 8px;
    background: #2563EB;
    color: #ffffff;
    font-size: 0.85rem;
    font-weight: 600;
    text-decoration: none;
    transition: background-color 0.15s ease;
    white-space: nowrap;

    &:hover {
      background: #1D4ED8;
      color: #ffffff;
      text-decoration: none;
    }

    &--done {
      background: #d1fae5;
      color: #065f46;
      cursor: default;

      &:hover {
        background: #d1fae5;
        color: #065f46;
      }
    }
  }
}

.webphone-intro {
  color: #6b7280;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 12px;
}

.webphone-checklist {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;

  &__item {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border-radius: 8px;
    border: 1px solid #eef0f4;
    background: #ffffff;
    transition: border-color 0.2s ease, background-color 0.2s ease;

    & + & {
      margin-top: 8px;
    }

    &--done {
      border-color: #a7f3d0;
      background: #f0fdf4;
    }

    &--waiting {
      border-color: #bfdbfe;
      background: #eff6ff;
    }
  }

  &__indicator {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    border-radius: 9999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.85rem;
    font-weight: 700;
    background: #eef0f4;
    color: #6b7280;

    i {
      font-size: 1rem;
    }

    .webphone-checklist__item--waiting & {
      background: #dbeafe;
      color: #2563EB;

      i {
        animation: webphone-spin 1.2s linear infinite;
      }
    }

    .webphone-checklist__item--done & {
      background: #16a34a;
      color: #ffffff;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  &__title {
    color: #111827;
    font-size: 0.95rem;
    font-weight: 600;
  }

  &__helper {
    color: #6b7280;
    font-size: 0.85rem;
    line-height: 1.4;
  }
}

.webphone-actions {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 8px;

  .onboarding-button {
    margin-top: 0;
  }

  &__hint {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0;
    font-size: 0.85rem;
    color: #6b7280;

    i {
      font-size: 1rem;
      color: #2563EB;
      animation: webphone-spin 1.2s linear infinite;
    }
  }
}

@keyframes webphone-spin {
  to { transform: rotate(360deg); }
}

.webphone-fallback {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #eef0f4;

  &__text {
    font-size: 0.85rem;
    color: #6b7280;
    margin: 0 0 10px;
  }

  .onboarding-button {
    margin-top: 0;
  }
}

.network-test-intro {
  color: #4b5563;
  font-size: 0.9rem;
  line-height: 1.5;
  margin: 0 0 16px;
}

.network-test-metrics-grid {
  list-style: none;
  margin: 0 0 16px;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;

  &--green .network-test-metric {
    background: #f0fdf4;
    border-color: #a7f3d0;

    .network-test-metric__value > span:not(.network-test-metric__unit):not(.network-test-metric__placeholder) {
      color: #16a34a;
    }
  }

  &--yellow .network-test-metric {
    background: #fffbeb;
    border-color: #fde68a;

    .network-test-metric__value > span:not(.network-test-metric__unit):not(.network-test-metric__placeholder) {
      color: #b45309;
    }
  }

  &--red .network-test-metric {
    background: #fef2f2;
    border-color: #fca5a5;

    .network-test-metric__value > span:not(.network-test-metric__unit):not(.network-test-metric__placeholder) {
      color: #dc2626;
    }
  }
}

.network-test-metric {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid #eef0f4;
  background: #ffffff;
  transition: background-color 0.2s ease, border-color 0.2s ease;

  &__label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #6b7280;
    font-size: 0.8rem;
    font-weight: 600;

    i { font-size: 0.9rem; }
  }

  &__info {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #9ca3af;
    cursor: help;
    transition: color 0.15s ease;
    outline: none;

    &:hover,
    &:focus {
      color: #2563EB;
    }

    &:hover .network-test-metric__tooltip,
    &:focus .network-test-metric__tooltip {
      opacity: 1;
      visibility: visible;
      transform: translateX(-50%) translateY(-4px);
    }
  }

  &__tooltip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    background: #111827;
    color: #ffffff;
    padding: 8px 12px;
    border-radius: 6px;
    font-size: 0.75rem;
    font-weight: 500;
    line-height: 1.4;
    width: max-content;
    max-width: 240px;
    text-align: left;
    white-space: normal;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.15s ease, transform 0.15s ease, visibility 0.15s ease;
    z-index: 20;

    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 5px solid transparent;
      border-top-color: #111827;
    }
  }

  &__value {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    font-size: 1.2rem;
    font-weight: 700;
    color: #111827;
  }

  &__unit {
    font-size: 0.75rem;
    font-weight: 600;
    color: #9ca3af;
  }

  &__placeholder {
    color: #9ca3af;
  }
}

.network-test-progress {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  margin: 4px 0 16px;

  &__bar {
    width: 100%;
    height: 6px;
    border-radius: 9999px;
    background: #eef0f4;
    overflow: hidden;
  }

  &__fill {
    height: 100%;
    border-radius: 9999px;
    background: linear-gradient(90deg, #6366f1 0%, #2563EB 100%);
    transition: width 0.2s ease;
  }

  &__text {
    color: #6b7280;
    font-size: 0.85rem;
  }
}

.network-test-summary {
  border-radius: 10px;
  border: 1px solid #eef0f4;
  background: #ffffff;
  padding: 14px 16px;
  margin-bottom: 12px;

  &__header {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 0.95rem;
    margin-bottom: 4px;

    i { font-size: 1.1rem; }
  }

  &__body {
    color: #4b5563;
    font-size: 0.85rem;
    line-height: 1.45;
    margin: 0;
  }

  &--green {
    background: #f0fdf4;
    border-color: #a7f3d0;
    color: #065f46;
    .network-test-summary__header i { color: #16a34a; }
    .network-test-summary__body { color: #065f46; }
  }

  &--yellow {
    background: #fffbeb;
    border-color: #fde68a;
    color: #92400e;
    .network-test-summary__header i { color: #f59e0b; }
    .network-test-summary__body { color: #92400e; }
  }

  &--red {
    background: #fef2f2;
    border-color: #fca5a5;
    color: #991b1b;
    .network-test-summary__header i { color: #dc2626; }
    .network-test-summary__body { color: #991b1b; }
  }

  &--error {
    background: #fff7ed;
    border-color: #fdba74;
    color: #9a3412;
    .network-test-summary__header i { color: #ea580c; }
    .network-test-summary__body { color: #9a3412; }
  }
}

.network-test-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;

  .onboarding-button {
    flex: 0 1 auto;
    margin-top: 0;

    > span {
      display: inline-flex;
      align-items: center;
      gap: 6px;
    }
  }

  &__support {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    color: #2563EB;
    font-size: 0.9rem;
    font-weight: 600;
    text-decoration: none;

    i { font-size: 1rem; }

    &:hover,
    &:focus {
      color: #1D4ED8;
      text-decoration: underline;
    }
  }
}

.step-dots {
  display: flex;
  gap: 4px;
  justify-content: center;
  margin: 20px auto 12px;
  width: 100%;
  max-width: 240px;

  &__dot {
    flex: 1;
    height: 3px;
    border-radius: 2px;
    background: #e5e7eb;

    &--active {
      background: #2563EB;
    }
  }
}

.step-footer {
  text-align: center;

  &__objective {
    font-size: 0.75rem;
    color: #6b7280;
    margin: 0 0 2px;

    strong {
      margin-left: 4px;
      color: #374151;
    }
  }

  &__help {
    font-size: 0.75rem;
    color: #2563EB;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/Onboarding/OnboardingWizard.vue